import { IndexLink } from 'react-router';
import {
  faChartColumn,
  faCompass,
  faGear,
  faHeadset,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { F0, TMap } from '../../helpers/types';
import auth from '@client/components/auth';
import { ReactComponent as CreatorsSvg } from '../../../images/menu/creators.svg';
import { ReactComponent as UsersSvg } from '../../../images/menu/users.svg';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { PATHS } from '../../../routes';
import { Version } from '../Version';
import { getRole } from './helpers';
import classNames from 'classnames';
import { PermissionScope } from '@playbook-technologies/common-enums';
import { isEmpty } from 'lodash';
import { useMenu } from '@client/new/components/Menu/useMenu';
import { ReactComponent as MenuSvg } from '../../../new/assets/icons/menu.svg';
import { ReactComponent as TimesSvg } from '../../../new/assets/icons/times.svg';
import { Link } from 'react-router';
import { ReactComponent as LogoSvg } from '../../../images/logo.svg';
import clsx from 'clsx';

export const DashboardMenu = ({
  logout,
  customPermissions,
}: {
  logout: () => void;
  customPermissions: Array<PermissionScope>;
}) => {
  const { open, toggle, close } = useMenu();
  const MobileMenuIcon = open ? TimesSvg : MenuSvg;
  return (
    <header className={`bg-white flex flex-col md:sticky md:top-0 md:h-screen`}>
      <div className="flex items-center justify-center py-5 px-8 lg:p-8">
        <Link className="text-gray-900 hidden md:block" to={PATHS.HOME}>
          <LogoSvg className="w-7 h-7 flex-no-shrink fill-current md:w-9 md:h-9" />
        </Link>
        <div
          className="md:hidden w-full flex justify-end col-span-1 cursor-pointer"
          onClick={toggle}
        >
          <MobileMenuIcon className="w-4 h-4 lg:w-4 lg:h-4 fill-zinc-900 group-hover:fill-primary" />
        </div>
      </div>
      <div
        className={clsx(
          'flex-1 h-full overflow-hidden',
          open && 'md:block',
          !open && 'hidden md:block',
        )}
      >
        <div
          className="h-full flex flex-col md:w-28 text-gray-700 bg-white flex-shrink-0 md:items-center"
          id="admin-menu"
        >
          <nav className="flex flex-col justify-between h-full">
            <DashboardMenuItems
              close={close}
              customPermissions={customPermissions}
            />
            <div
              className="md:py-5 md:text-center lg:self-end"
              onClick={logout}
            >
              <a
                className={`block px-4 py-2 mt-2 text-red-500 text-sm rounded-lg focus:text-gray-900  focus:outline-none focus:shadow-outline `}
                href="#"
              >
                <div className="flex gap-4 md:gap-2 md:flex-col md:items-center">
                  <span
                    className="text-bold text-xl  md:text-base"
                    style={{ color: '#6367ef' }}
                  >
                    Sign Out
                  </span>
                </div>
              </a>
              <Version />
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};

const FontIcon = (icon: IconProp) => () =>
  <FontAwesomeIcon icon={icon} color="#8A8F99" />;

const AnalyticsSvg = FontIcon(faChartColumn);
const SettingsSvg = FontIcon(faGear);
const CmsSvg = FontIcon(faCompass);
const SupportToolSvg = FontIcon(faHeadset);

const menuItem = (
  pathName: keyof typeof PATHS,
  Icon: React.FC<React.SVGProps<SVGSVGElement>>,
  onClick?: F0,
): MenuLinkProps => ({ to: PATHS[pathName] as string, Icon, onClick });

type Role = ReturnType<typeof getRole>;

const permissionItemsMap = (customPermissions: Array<PermissionScope>) => {
  const permissionItems = [];
  if (
    !isEmpty(customPermissions) &&
    customPermissions.includes(PermissionScope.InternalTools)
  ) {
    permissionItems.push('Discovery', 'Support');
  }
  return permissionItems;
};

const DashboardMenuItems = ({
  close,
  customPermissions,
}: {
  close: () => void;
  customPermissions: Array<PermissionScope>;
}) => {
  const menuItems: { [key: string]: any } = {
    Creators: menuItem('HOME', CreatorsSvg),
    Analytics: menuItem('ANALYTICS', AnalyticsSvg),
    Users: menuItem('PORTAL_USERS', UsersSvg),
    Config: menuItem('CONFIG_FILE', SettingsSvg),
    Discovery: menuItem('Discovery' as any, CmsSvg, () => {
      const CMS_URL = import.meta.env.VITE_CMS_URL;
      const event = { isLoginData: true, data: auth.getUser() };
      const iframe = document.createElement('iframe');
      iframe.src = CMS_URL;
      iframe.width = '0px';
      iframe.height = '0px';
      window.document.body.appendChild(iframe);
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.postMessage(event, CMS_URL);
          setTimeout(() => {
            const w = window.open(CMS_URL + '/discovery', '_blank');
            w?.focus();
            document.removeChild(iframe);
          }, 500);
        }, 500);
      };
    }),
    Support: menuItem('SUPPORT_TOOL' as any, SupportToolSvg, () => {
      const SUPPORT_TOOL_URL = import.meta.env.VITE_SUPPORT_TOOL;
      const event = { isLoginData: true, data: auth.getUser() };
      const iframe = document.createElement('iframe');
      iframe.src = SUPPORT_TOOL_URL;
      iframe.width = '0px';
      iframe.height = '0px';
      window.document.body.appendChild(iframe);
      iframe.onload = () => {
        setTimeout(() => {
          iframe.contentWindow?.postMessage(event, SUPPORT_TOOL_URL);
          setTimeout(() => {
            const w = window.open(SUPPORT_TOOL_URL, '_blank');
            w?.focus();
            document.removeChild(iframe);
          }, 500);
        }, 500);
      };
    }),
  };
  const roleItemsMap: TMap<Role, Array<keyof typeof menuItems>> = {
    admin: ['Creators', 'Analytics', 'Users', 'Config'],
    moderator: ['Creators', 'Analytics'],
    contentEditor: ['Creators'],
    unknown: [],
  };
  const items = [
    ...roleItemsMap[getRole()],
    ...permissionItemsMap(customPermissions),
  ];
  return (
    <div onClick={close}>
      {items.map((name) => (
        <MenuLink {...menuItems[name]} key={name}>
          {name}
        </MenuLink>
      ))}
    </div>
  );
};

type MenuLinkProps = {
  to: string;
  children?: React.ReactNode;
  Icon?: React.FC<React.SVGProps<SVGSVGElement>>;
  isActiveClassDisabled?: boolean;
  onClick?: F0;
};

const MenuLink = ({
  to,
  children,
  Icon,
  isActiveClassDisabled,
  onClick,
}: MenuLinkProps) => {
  return (
    <IndexLink
      onClick={onClick}
      activeClassName={classNames({
        'font-black text-primary group group-active menu-item-active':
          !isActiveClassDisabled,
      })}
      className={`cursor-pointer block group px-4 py-2 text-black text-sm text-center rounded-lg hover:text-primary focus:outline-none focus:shadow-outline `}
      to={to}
    >
      <div className="flex gap-4 items-center md:gap-2 md:flex-col">
        {Icon && (
          <div className="text-[24px] md:text-[28px] min-w-[24px]">
            <Icon className="w-6 h-6 md:w-7 md:h-7 fill-gray-400  group-hover:fill-primary" />
          </div>
        )}
        <span className="text-xl md:text-sm">{children}</span>
      </div>
    </IndexLink>
  );
};
